import type { AgentDto } from '@nova/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/Button.js';
import { api } from '../../lib/api.js';

/**
 * The numeric knobs that decide how the agents behave, editable where the agent
 * is — not in code (dokumentasjon/07-utviklingsplan.md 2.1.5).
 *
 * Only the settings a marketing person has a reason to change are listed. The
 * rest of the config stays in the database untouched; a partial save would wipe
 * it, so the form always writes back the whole object.
 */
const KNOBS: Record<string, { key: string; label: string; help: string; max: number }[]> = {
  planner: [
    {
      key: 'coverageWeeks',
      label: 'Dekningsvindu (uker)',
      help: 'Hvor langt fram Nova måler om planen har hull.',
      max: 52,
    },
    {
      key: 'minPlannedPerArea',
      label: 'Minst planlagt per produktområde',
      help: 'Antall saker med publiseringsdato inne i vinduet.',
      max: 100,
    },
    {
      key: 'minPlannedPerCompanyProfile',
      label: 'Minst planlagt per profil',
      help: 'Selskaps- og ansattprofiler publiserer oftere enn produktområdene.',
      max: 100,
    },
    {
      key: 'maxSuggestionsPerRun',
      label: 'Maks forslag per kjøring',
      help: 'Bremsen som hindrer at planen fylles opp på én kjøring.',
      max: 20,
    },
  ],
  writer: [
    {
      key: 'draftAheadWeeks',
      label: 'Skriv utkast inntil (uker) før publisering',
      help: 'Forslag som nærmer seg datoen får utkast selv om de ikke er bestilt. Koster tokens på tekst som kan bli avvist — til gjengjeld er det lettere å ta stilling.',
      max: 26,
    },
    {
      key: 'maxDraftsPerRun',
      label: 'Maks utkast per kjøring',
      help: 'Hvor mye Nova skriver om gangen.',
      max: 20,
    },
  ],
};

export function AgentSettings({ agent }: { agent: AgentDto }) {
  const queryClient = useQueryClient();
  const knobs = KNOBS[agent.key];

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries((knobs ?? []).map((k) => [k.key, String(agent.config[k.key] ?? '')])),
  );
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const next: Record<string, unknown> = { ...agent.config };
      for (const knob of knobs ?? []) {
        const parsed = Number(values[knob.key]);
        if (Number.isFinite(parsed) && parsed > 0) next[knob.key] = parsed;
      }
      return api.patch(`/agents/${agent.key}`, { config: next });
    },
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });

  if (!knobs) return null;

  return (
    <div className="border-hairline bg-base/30 mt-3 rounded-lg border p-3">
      <p className="label-micro mb-2">Innstillinger</p>

      <div className="grid gap-3 sm:grid-cols-2">
        {knobs.map((knob) => (
          <div key={knob.key}>
            <label className="text-ink block text-[11px]" htmlFor={`${agent.key}-${knob.key}`}>
              {knob.label}
            </label>
            <input
              id={`${agent.key}-${knob.key}`}
              type="number"
              min={1}
              max={knob.max}
              value={values[knob.key] ?? ''}
              onChange={(e) => setValues({ ...values, [knob.key]: e.target.value })}
              className="border-hairline bg-base/40 text-ink num-tabular mt-1 h-8 w-24 rounded-lg border px-2 text-xs"
            />
            <p className="text-ink-soft/70 mt-1 text-[11px] leading-snug">{knob.help}</p>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-3">
        <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
          Lagre innstillinger
        </Button>
        {saved && (
          <span className="text-success inline-flex items-center gap-1 text-[11px]">
            <Check size={12} aria-hidden />
            Lagret — gjelder fra neste kjøring
          </span>
        )}
        {save.error && <span className="text-danger text-[11px]">{save.error.message}</span>}
      </div>
    </div>
  );
}
